import { getGlobal } from "@nevoland/get-global";

import type { UserModel } from "../types";

export type UserOfferFields = {
  offerType?: string;
  offerClass?: string | null;
};

/** Reads offer fields from portal user JSON (`flags` or top-level CurrentUser fields). */
export function offerFieldsFromUser(
  parsedUser: UserModel & UserOfferFields,
): UserOfferFields {
  const offerType =
    typeof parsedUser.flags?.OFFER_TYPE === "string"
      ? parsedUser.flags.OFFER_TYPE
      : typeof parsedUser.offerType === "string"
        ? parsedUser.offerType
        : undefined;

  const rawOfferClass =
    typeof parsedUser.flags?.OFFER_CLASS === "string"
      ? parsedUser.flags.OFFER_CLASS
      : parsedUser.offerClass;

  const offerClass = typeof rawOfferClass === "string" ? rawOfferClass : null;

  return { offerType, offerClass };
}

/** Minimal `CurrentUser` shape used by `RegisterUtils.getAvailableSystemFamilies`. */
type RegistrationLegacyUser = {
  offerType?: string;
  offerClass?: string | null;
  isALMSC(): boolean;
};

type LegacyRegisterUtils = {
  getAvailableSystemFamilies: (user: RegistrationLegacyUser) => string[];
};

type SystemTypeDefinition = {
  id: string;
  defaultApplicationTypes?: string[];
};

export type LegacySystemTypesModule = {
  SUPPORTED_FAMILIES_AND_TYPES: Record<string, readonly string[]>;
  getSystemTypes: (systemTypeFamily: string) => SystemTypeDefinition[];
};

type LegacyRequire = (moduleId: string) => unknown;

function legacyRequireSync<T>(moduleId: string): T | undefined {
  const requireFn = (getGlobal() as { require?: LegacyRequire }).require;
  if (typeof requireFn !== "function") {
    return undefined;
  }

  try {
    return requireFn(moduleId) as T;
  } catch {
    return undefined;
  }
}

function getLegacyRegisterUtils(): LegacyRegisterUtils | undefined {
  return legacyRequireSync<LegacyRegisterUtils>(
    "app/domain/offerType/registerUtils",
  );
}

export function getLegacySystemTypes(): LegacySystemTypesModule | undefined {
  return legacyRequireSync<LegacySystemTypesModule>(
    "app/domain/system/systemTypes",
  );
}

export function registrationUserFromViewContext(context: {
  offerType?: string;
  offerClass?: string | null;
}): RegistrationLegacyUser {
  const { offerType, offerClass } = context;
  return {
    offerType,
    offerClass,
    isALMSC() {
      return (
        offerType === "AVC" &&
        offerClass != null &&
        ["ALMSC", "ALMSC_Reseller"].includes(offerClass)
      );
    },
  };
}

export type RegistrationDeviceModelOption = {
  /** Gateway model value stored on group rules (uppercase, e.g. XR60). */
  value: string;
  /** Registration UI label (e.g. AirLink XR60). */
  label: string;
  /** Registration system type id (lowercase, e.g. xr60). */
  systemTypeId: string;
};

function legacyPortalLabel(key: string): string | undefined {
  const i18n = (getGlobal() as { i18n?: (messageKey: string) => string }).i18n;
  if (typeof i18n !== "function") {
    return undefined;
  }

  const label = i18n(key);
  return label && label !== key ? label : undefined;
}

/**
 * Labels use the same keys as the registration page: `SYSTEM_TYPE_<typeId>`.
 */
export function registrationSystemTypeLabel(systemTypeId: string): string {
  const key = `SYSTEM_TYPE_${systemTypeId}`;
  return legacyPortalLabel(key) ?? systemTypeId;
}

/** Registration uses lowercase type ids; group rules store uppercase gateway models. */
export function registrationTypeIdToGatewayModel(systemTypeId: string): string {
  return systemTypeId.toUpperCase();
}

export function registrationGatewayModelToTypeId(gatewayModel: string): string {
  return gatewayModel.toLowerCase();
}

export function getRegistrationDeviceModelOptions(
  user: RegistrationLegacyUser,
): readonly RegistrationDeviceModelOption[] {
  const registerUtils = getLegacyRegisterUtils();
  const systemTypes = getLegacySystemTypes();
  if (registerUtils == null || systemTypes == null) {
    return [];
  }

  const options: RegistrationDeviceModelOption[] = [];
  const seenValues = new Set<string>();

  for (const family of registerUtils.getAvailableSystemFamilies(user)) {
    const typeIds = systemTypes.SUPPORTED_FAMILIES_AND_TYPES[family];
    if (typeIds == null) {
      continue;
    }

    for (const systemTypeId of typeIds) {
      const value = registrationTypeIdToGatewayModel(systemTypeId);
      if (seenValues.has(value)) {
        continue;
      }

      seenValues.add(value);
      options.push({
        value,
        label: registrationSystemTypeLabel(systemTypeId),
        systemTypeId,
      });
    }
  }

  return options.sort((left, right) =>
    left.label.localeCompare(right.label, undefined, { sensitivity: "base" }),
  );
}

export function getRegistrationDeviceModelValueSet(
  user: RegistrationLegacyUser,
): ReadonlySet<string> {
  return new Set(
    getRegistrationDeviceModelOptions(user).map((option) => option.value),
  );
}

export function registrationDeviceModelLabel(
  gatewayModel: string,
  options: readonly RegistrationDeviceModelOption[],
): string {
  const match = options.find((option) => option.value === gatewayModel);
  if (match != null) {
    return match.label;
  }

  return registrationSystemTypeLabel(
    registrationGatewayModelToTypeId(gatewayModel),
  );
}

export function availableRegistrationDeviceModels(
  mappings: readonly { deviceModel: string }[],
  allOptions: readonly RegistrationDeviceModelOption[],
): readonly RegistrationDeviceModelOption[] {
  const usedModels = new Set(mappings.map((mapping) => mapping.deviceModel));
  return allOptions.filter((option) => !usedModels.has(option.value));
}

/** Generic catch-all model; not offered in fleet group rule editor add lists. */
export const GROUP_RULE_EDITOR_EXCLUDED_DEVICE_MODEL_VALUES = new Set([
  "GENERICAIRLINK",
]);

export function filterGroupRuleEditorDeviceModelOptions(
  options: readonly RegistrationDeviceModelOption[],
): readonly RegistrationDeviceModelOption[] {
  return options.filter(
    (option) => !GROUP_RULE_EDITOR_EXCLUDED_DEVICE_MODEL_VALUES.has(option.value),
  );
}
